import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Select, Button, Switch, message, Divider, Space, Tag, Typography, InputNumber } from 'antd';
import { MailOutlined, SaveOutlined, SendOutlined, SettingOutlined, SafetyOutlined } from '@ant-design/icons';
import api from '../services/api';

const { Title, Text } = Typography;

const AdminSMTP = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [twofaEnabled, setTwofaEnabled] = useState(true);
  const [toggling2FA, setToggling2FA] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/smtp-config');
      if (data.config) {
        form.setFieldsValue({
          smtp_host: data.config.smtp_host || '',
          smtp_port: parseInt(data.config.smtp_port) || 587,
          smtp_user: data.config.smtp_user || '',
          smtp_pass: data.config.smtp_pass || '',
          smtp_from_email: data.config.smtp_from_email || '',
          smtp_from_name: data.config.smtp_from_name || '',
          smtp_encryption: data.config.smtp_encryption || 'tls',
        });
      }
    } catch {
      message.error('Error al cargar la configuración');
    }
    setLoading(false);
  };

  const handleSave = async (values) => {
    setSaving(true);
    try {
      await api.post('/admin/smtp-config', values);
      message.success('Configuración SMTP guardada');
    } catch {
      message.error('Error al guardar');
    }
    setSaving(false);
  };

  const handleTest = async () => {
    if (!testEmail) { message.warning('Ingresa un email de prueba'); return; }
    setTesting(true);
    try {
      const { data } = await api.post('/admin/smtp-test', { email: testEmail });
      if (data.success) {
        message.success(data.message);
      } else {
        message.error(data.message || 'Error al enviar');
      }
    } catch (e) {
      message.error(e.response?.data?.error || 'Error al enviar email de prueba');
    }
    setTesting(false);
  };

  const handleToggle2FA = async (enabled) => {
    setToggling2FA(true);
    try {
      await api.post('/admin/toggle-2fa', { enabled });
      setTwofaEnabled(enabled);
      message.success(`2FA ${enabled ? 'activado' : 'desactivado'}`);
    } catch {
      message.error('Error al cambiar el estado de 2FA');
    }
    setToggling2FA(false);
  };

  const cardStyle = {
    background: '#ffffff',
    border: '1px solid #e9edf4',
    borderRadius: 16,
    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
  };

  return (
    <div className="page-container fade-in">
    <div style={{ maxWidth: 700, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={3} style={{ color: '#232a3e', margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
          <SettingOutlined style={{ color: '#845adf' }} /> Configuración SMTP & 2FA
        </Title>
        <Text style={{ color: '#5b6b8a' }}>Configura el servidor de correo y la autenticación de dos factores</Text>
      </div>

      {/* 2FA Toggle */}
      <Card style={{ ...cardStyle, marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <SafetyOutlined style={{ fontSize: 24, color: '#845adf' }} />
            <div>
              <Text strong style={{ color: '#232a3e', fontSize: 15 }}>Autenticación de Dos Factores</Text>
              <br />
              <Text style={{ color: '#5b6b8a', fontSize: 12 }}>Envía un código por email al iniciar sesión</Text>
            </div>
          </div>
          <Space>
            <Tag color={twofaEnabled ? 'green' : 'default'}>{twofaEnabled ? 'Activo' : 'Inactivo'}</Tag>
            <Switch checked={twofaEnabled} onChange={handleToggle2FA} loading={toggling2FA} />
          </Space>
        </div>
      </Card>

      {/* SMTP Config */}
      <Card style={cardStyle} loading={loading}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <MailOutlined style={{ fontSize: 20, color: '#845adf' }} />
          <Text strong style={{ color: '#232a3e', fontSize: 16 }}>Servidor SMTP</Text>
        </div>

        <Form form={form} layout="vertical" onFinish={handleSave} requiredMark={false}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <Form.Item name="smtp_host" label={<Text style={{ color: '#8c9fc5' }}>Host</Text>} rules={[{ required: true }]}>
              <Input placeholder="smtp.gmail.com" />
            </Form.Item>
            <Form.Item name="smtp_port" label={<Text style={{ color: '#8c9fc5' }}>Puerto</Text>} rules={[{ required: true }]}>
              <InputNumber style={{ width: '100%' }} placeholder="587" />
            </Form.Item>
            <Form.Item name="smtp_user" label={<Text style={{ color: '#8c9fc5' }}>Usuario</Text>} rules={[{ required: true }]}>
              <Input placeholder="tu@email.com" />
            </Form.Item>
            <Form.Item name="smtp_pass" label={<Text style={{ color: '#8c9fc5' }}>Contraseña</Text>} rules={[{ required: true }]}>
              <Input.Password placeholder="App password" />
            </Form.Item>
            <Form.Item name="smtp_from_email" label={<Text style={{ color: '#8c9fc5' }}>Email remitente</Text>}>
              <Input placeholder="noreply@tudominio.com" />
            </Form.Item>
            <Form.Item name="smtp_from_name" label={<Text style={{ color: '#8c9fc5' }}>Nombre remitente</Text>}>
              <Input placeholder="SmartAdmin" />
            </Form.Item>
          </div>
          <Form.Item name="smtp_encryption" label={<Text style={{ color: '#8c9fc5' }}>Encriptación</Text>}>
            <Select options={[{ value: 'tls', label: 'TLS (587)' }, { value: 'ssl', label: 'SSL (465)' }, { value: 'none', label: 'Sin encriptación' }]} />
          </Form.Item>

          <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving}
            style={{ background: 'linear-gradient(135deg, #845adf, #6c3fc5)', border: 'none', borderRadius: 8 }}>
            Guardar Configuración
          </Button>
        </Form>

        <Divider style={{ borderColor: '#e9edf4' }} />

        {/* Test Email */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <SendOutlined style={{ color: '#845adf' }} />
          <Text strong style={{ color: '#232a3e' }}>Enviar Email de Prueba</Text>
        </div>
        <Space.Compact style={{ width: '100%' }}>
          <Input placeholder="email@ejemplo.com" value={testEmail} onChange={e => setTestEmail(e.target.value)} style={{ flex: 1 }} />
          <Button type="primary" icon={<SendOutlined />} onClick={handleTest} loading={testing}
            style={{ background: '#845adf', border: 'none' }}>
            Enviar
          </Button>
        </Space.Compact>
      </Card>
    </div>
    </div>
  );
};

export default AdminSMTP;
